import { gettext } from '@eventbrite/i18n';
import merge from 'lodash/merge';
import transform from '../transform';
import { FEATURE_FLAG_OVERRIDES } from './constants';
import {
    LOCALE_WITH_VERSION_2,
    PACKAGE_ONE,
    PACKAGE_VERSION_1,
} from '../../constants/main';
import { STYLE_ANCHOR } from '@eventbrite/eds-button';
import pricingAndPackagingSettings from './pricingAndPackagingSettings';
/**
 * Some sections in packages change according to the country and
 * a FF condition. This function merges conditional sections
 * for packages, depending on locale and feature flag
 *
 * @param  {object} settings default settings for the page
 * @param  {bool} featureFlag if enabled, changes will be merged with default settings
 * @return {object} newly transformed settings with all of the settings merged
 */
export const packageSectionsTransform = (settings) => {
    const {
        locale,
        page,
        userPlan,
        packageVersion,
        launchNewPackageAndPricing,
    } = settings;
    const transformSettings = transform(settings);

    if (!launchNewPackageAndPricing) {
        if (
            LOCALE_WITH_VERSION_2.includes(locale) &&
            FEATURE_FLAG_OVERRIDES[locale] &&
            FEATURE_FLAG_OVERRIDES[locale][page]
        ) {
            transformSettings['sections'] = merge(
                transformSettings['sections'],
                FEATURE_FLAG_OVERRIDES[locale][page]['sections'],
            );
            if (
                userPlan === PACKAGE_ONE &&
                packageVersion === PACKAGE_VERSION_1
            ) {
                transformSettings['CTAs'] =
                    FEATURE_FLAG_OVERRIDES[locale][page]['CTAs'];
                transformSettings['CTAStyles'] =
                    FEATURE_FLAG_OVERRIDES[locale][page]['CTAStyles'];
            }
        }
    }

    return transformSettings;
};

// JIRA EB-204454 / launchNewPackageAndPricing
export const getNewPackageAndPricingOverrides = ({
    tld,
    launchNewPackageAndPricing,
    feeBreakdown,
    defaultSettings,
    packages,
    packageTypes,
    isAuthenticated,
    userPlan,
    showPricingAndPackagingContent,
}) => {
    const feeBreakdownOverride = feeBreakdown;
    let packagesOverride = packages;
    let packageTypesOverride = packageTypes;
    let defaultSettingsOverride = defaultSettings;

    if (launchNewPackageAndPricing) {
        // overrides based on FF
        feeBreakdown.ticket.package3.formattedProcessingFee = gettext('Custom');
        packagesOverride = packages.slice(1);
        packageTypesOverride = packageTypes.slice(1);

        // remove package from section features
        defaultSettingsOverride = getNewPackageAndPricingSettings(
            defaultSettings,
            isAuthenticated,
            userPlan,
        );
    }

    if (showPricingAndPackagingContent) {
        packagesOverride = [gettext('Flex or Pro'), gettext('Premium')];
        packageTypesOverride = packageTypes.slice(1);

        defaultSettingsOverride = pricingAndPackagingSettings;
    }

    return {
        tld,
        launchNewPackageAndPricing,
        feeBreakdownOverride,
        packagesOverride,
        packageTypesOverride,
        defaultSettingsOverride,
    };
};

export const getFormattedRow = (
    title,
    link = '',
    perPlanData = [false, true],
) => {
    return {
        title,
        link: link,
        perPlanData,
    };
};

export const getPackageText = ({ isAuthenticated, userPlan }) => {
    let PACKAGE_ONE_CTA_TEXT = gettext('Get started for free');
    let PACKAGE_TWO_CTA_TEXT = gettext('Contact Sales');
    let CTAStyles = [STYLE_ANCHOR, STYLE_ANCHOR];

    if (isAuthenticated) {
        if (userPlan === 'package2') {
            PACKAGE_ONE_CTA_TEXT = gettext('Your current package');
            PACKAGE_TWO_CTA_TEXT = gettext('Contact sales');
            CTAStyles = [null, STYLE_ANCHOR];
        }
        if (userPlan === 'package3') {
            PACKAGE_ONE_CTA_TEXT = '';
            PACKAGE_TWO_CTA_TEXT = gettext('Your current package');
            CTAStyles = [STYLE_ANCHOR, null];
        }
    }

    return { PACKAGE_ONE_CTA_TEXT, PACKAGE_TWO_CTA_TEXT, CTAStyles };
};
// JIRA EB-204454 / launchNewPackageAndPricing
export const getNewPackageAndPricingSettings = (
    defaultSettings,
    isAuthenticated,
    userPlan,
) => {
    const { PACKAGE_ONE_CTA_TEXT, PACKAGE_TWO_CTA_TEXT, CTAStyles } =
        getPackageText({
            userPlan,
            isAuthenticated,
        });

    const newSettings = {
        0: [
            gettext('Customized onboarding and implementation services'),
            gettext('1:1 consultations with event experts'),
            gettext('Real-time access to support experts'),
            gettext('Collect additional revenue per ticket (royalties)'),
        ],
        1: [gettext('Early access to new Eventbrite features')],
        3: [gettext('Manage multiple organizations from one account')],
    };

    // SECTION: 'Start Selling'
    const startSellingSectionFeatures = defaultSettings.sections[0].features;
    // changing TICKET_TYPES to checkmarks
    startSellingSectionFeatures[0].perPlanData = [true, true, true];
    // remove pass on fees
    startSellingSectionFeatures.splice(8, 1);
    // removing group registration
    startSellingSectionFeatures.pop();

    // SECTION: 'Stay Secure'
    const staySecureSectionFeatures = defaultSettings.sections[4].features;
    // removing schedule payouts
    staySecureSectionFeatures.pop();

    const newSections = Object.values(defaultSettings['sections']).map(
        (section, idx) => {
            const updatedFeatures = section.features.map((feature) => ({
                ...feature,
                perPlanData: feature.perPlanData.slice(1),
            }));

            let newFeatures = [];
            if (newSettings[idx]) {
                newFeatures = newSettings[idx].map((s) => getFormattedRow(s));
            }

            return {
                ...section,
                features: [...newFeatures, ...updatedFeatures],
            };
        },
    );

    const defaultSettingsOverride = {
        CTAs: [PACKAGE_ONE_CTA_TEXT, PACKAGE_TWO_CTA_TEXT],
        CTAStyles,
        paymentProcessingFeature: {
            title: gettext('Payment processing fee per order'),
            link: '/support/articleredirect?anum=4373',
        },
        sections: newSections,
    };
    return { ...defaultSettings, ...newSettings, ...defaultSettingsOverride };
};
